import { useEffect, useMemo, useState } from 'react';
import { Bell, CheckCheck, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  PopoverArrow,
} from '@/components/ui/popover';

import {
  useFetchNotificationsQuery,
  useLazyFetchNotificationsQuery,
  useMarkAllAsReadMutation,
} from '@/state/notifications/notification-api';
import { useGetCurrentUserQuery } from '@/state/user';
import { transformRealToUI } from '@/lib/notification-utils';
import { NotificationList } from '@/components/notifications/notification-list';

interface NotificationHeaderProps {
  unreadCount?: number;
  className?: string;
}

export function NotificationHeader({
  unreadCount: manualUnreadCount,
  className,
}: NotificationHeaderProps) {
  const navigate = useNavigate();
  const { tenant } = useTenant();
  const { data: user } = useGetCurrentUserQuery({ tenant });
  const [open, setOpen] = useState(false);
  const [fetchNotifications, { data: previewData, isFetching, isLoading }] =
    useLazyFetchNotificationsQuery();
  const [markAllAsRead] = useMarkAllAsReadMutation();

  // Fetch only unread notifications to get the count
  const { data: unreadData } = useFetchNotificationsQuery(
    {
      tenant: tenant!,
      userId: user?.id || '',
      isRead: false,
    },
    { skip: !user?.id }
  );

  const unreadCount =
    manualUnreadCount !== undefined
      ? manualUnreadCount
      : unreadData?.count || 0;

  const previewNotifications = useMemo(() => {
    const results = previewData?.results || [];
    return results.slice(0, 10).map(transformRealToUI);
  }, [previewData]);

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (nextOpen && tenant && user?.id) {
      fetchNotifications(
        {
          tenant,
          userId: user.id,
          page: 1,
          isRead: undefined,
        },
        false
      );
    }
  };

  useEffect(() => {
    if (!open) return;
    const topNotification = previewNotifications[0];
    if (topNotification?.status === 'unread' && tenant) {
      markAllAsRead({ tenant }).catch(() => {
        // ignore auto-mark failures; user can still use the popover
      });
    }
  }, [open, previewNotifications, markAllAsRead, tenant]);

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative h-10 w-10 rounded-xl hover:bg-muted/50 transition-all duration-200"
          >
            <Bell className="h-5 w-5 text-muted-foreground" />
            {unreadCount > 0 && (
              <Badge
                className="absolute -top-1 -right-1 min-w-5 h-5 px-1 flex items-center justify-center
             bg-primary text-primary-foreground border-2 border-background
             animate-in zoom-in duration-300 text-xs"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </Badge>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          side="bottom"
          sideOffset={10}
          alignOffset={4}
          collisionPadding={12}
          className="w-[min(92vw,420px)] max-w-[min(92vw,420px)] p-0 overflow-x-hidden overflow-y-hidden rounded-[28px] border-border/60 bg-background shadow-2xl"
        >
          <PopoverArrow className="fill-border/60" />
          <div className="flex items-center justify-between border-b border-border/60 px-4 py-3">
            <div>
              <p className="text-sm font-semibold">Notifications</p>
              <p className="text-[11px] text-muted-foreground">
                Showing your latest 10 updates
              </p>
            </div>
            {(isLoading || isFetching) && (
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto overflow-x-hidden p-2">
            <NotificationList
              notifications={previewNotifications}
              currentUserId={user?.id}
              onNotificationClick={(id) => {
                const item = previewNotifications.find((n) => n.id === id);
                if (item?.link) {
                  navigate(item.link);
                }
              }}
            />
          </div>

          <div className="border-t border-border/60 p-2">
            <Button
              variant="ghost"
              className="w-full justify-between rounded-2xl"
              onClick={() => {
                setOpen(false);
                navigate(`/${tenant}/notifications`);
              }}
            >
              <span className="inline-flex items-center gap-2">
                <CheckCheck className="h-4 w-4" />
                View all notifications
              </span>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
